import { Link, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { Trophy, ArrowsLeftRight, UsersThree, Scales, Tray, UsersFour, ListBullets, X, Target, SignOut, ShieldCheck, Binoculars, ChartLineUp, Lightning, ClockCounterClockwise, Clipboard, Medal, CaretDown, Wrench, Football, PencilSimple, GoogleLogo, User, Envelope, Key, BookOpen, Newspaper, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const navGroups = [
  {
    label: "TRADES",
    icon: ArrowsLeftRight,
    items: [
      { path: "/trade", label: "AI Analyzer", desc: "AI-powered trade suggestions", icon: ArrowsLeftRight },
      { path: "/finder", label: "Trade Finder", desc: "Auto-scan for mutual-fit trades", icon: Binoculars },
      { path: "/target", label: "Target a Player", desc: "Realistic offers to land who you want", icon: Target },
      { path: "/simulator", label: "Simulator", desc: "Before/after trade impact", icon: ChartLineUp },
      { path: "/calculator", label: "Calculator", desc: "Manual value comparison", icon: Scales },
      { path: "/evaluate", label: "Evaluator", desc: "Quick trade fairness check", icon: Clipboard },
      { path: "/inbox", label: "Trade Inbox", desc: "Saved trade suggestions", icon: Tray },
    ],
  },
  {
    label: "LEAGUE",
    icon: UsersThree,
    items: [
      { path: "/teams", label: "All Teams", desc: "Rosters & positional rankings", icon: UsersThree },
      { path: "/contender", label: "Contender Rankings", desc: "Win-now vs rebuilder scores", icon: Medal },
      { path: "/history", label: "League History", desc: "Multi-season standings", icon: ClockCounterClockwise },
    ],
  },
  {
    label: "DRAFT",
    icon: Lightning,
    items: [
      { path: "/draft-room", label: "War Room", desc: "Pick advisor & draft board", icon: Lightning },
      { path: "/mock-draft", label: "Mock Draft", desc: "Simulate the full draft", icon: Football },
    ],
  },
  {
    label: "TOOLS",
    icon: Wrench,
    items: [
      { path: "/compare", label: "Player Compare", desc: "Side-by-side player values", icon: UsersFour },
      { path: "/optimizer", label: "Roster Optimizer", desc: "AI-built trade plan", icon: Target },
      { path: "/referral", label: "Refer Friends", desc: "Share & earn rewards", icon: UsersThree },
    ],
  },
  {
    label: "CONTENT",
    icon: BookOpen,
    items: [
      { path: "/rankings", label: "Value Chart", desc: "Live dynasty player values", icon: ChartLineUp },
      { path: "/rookie-rankings", label: "Rookie Rankings", desc: "Tiered rookie class", icon: Lightning },
      { path: "/guide", label: "Guides", desc: "Dynasty strategy articles", icon: BookOpen },
      { path: "/blog", label: "Blog", desc: "Weekly columns & analysis", icon: Newspaper },
      { path: "/research", label: "Research", desc: "Championship data analysis", icon: ChartLineUp },
    ],
  },
];

function DesktopDropdown({ group, isActive, currentPath }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const hasActive = group.items.some(i => currentPath === i.path);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const Icon = group.icon;
  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold uppercase tracking-wider transition-colors rounded-sm
          ${hasActive ? 'text-[#ccff00] bg-[#ccff00]/10' : 'text-[#a3a3a3] hover:text-white'}`}
        data-testid={`nav-group-${group.label.toLowerCase()}`}
      >
        <Icon size={15} weight="bold" />
        {group.label}
        <CaretDown size={10} weight="bold" className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 w-64 bg-[#171717] border border-[#262626] shadow-xl z-50 py-1" data-testid={`dropdown-${group.label.toLowerCase()}`}>
          {group.items.map(({ path, label, desc, icon: ItemIcon }) => (
            <Link
              key={path}
              to={path}
              onClick={() => setOpen(false)}
              className={`flex items-start gap-3 px-4 py-2.5 no-underline transition-colors
                ${currentPath === path ? 'bg-[#ccff00]/10 text-[#ccff00]' : 'text-white hover:bg-[#262626]'}`}
              data-testid={`nav-link-${label.toLowerCase().replace(/\s/g, '-')}`}
            >
              <ItemIcon size={16} weight="bold" className="mt-0.5 shrink-0 text-[#666]" />
              <div>
                <p className="text-sm font-semibold">{label}</p>
                <p className="text-[10px] text-[#666] mt-0.5">{desc}</p>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function UserMenu({ user, handleLogout }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen(!open)} className="flex items-center gap-1.5 px-2 py-1 rounded-sm hover:bg-[#171717] transition-colors" data-testid="user-menu-btn" aria-label="Account menu">
        {user.picture ? (
          <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />
        ) : (
          <div className="w-7 h-7 rounded-full bg-[#262626] flex items-center justify-center text-[10px] font-bold text-[#a3a3a3]">
            {((user.name || "?")[0] || "?").toUpperCase()}
          </div>
        )}
        <CaretDown size={10} weight="bold" className="text-[#666]" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-56 bg-[#171717] border border-[#262626] shadow-xl z-50 py-1 max-h-[80vh] overflow-y-auto" style={{ maxWidth: 'calc(100vw - 16px)' }}>
          <div className="px-4 py-3 border-b border-[#262626]">
            <p className="text-sm font-semibold text-white truncate">{user.name}</p>
            <p className="text-[10px] text-[#666] truncate">{user.email}</p>
            {user.sleeper_username && <p className="text-[10px] text-[#ccff00] mt-0.5">Sleeper: {user.sleeper_username}</p>}
            <p className="text-[8px] font-bold uppercase mt-1 px-1.5 py-0.5 inline-block rounded-sm" style={{ color: user.is_admin ? '#ccff00' : '#a3a3a3', backgroundColor: user.is_admin ? '#ccff0015' : '#26262680' }}>
              {user.is_admin ? 'ADMIN' : user.subscription_status === 'active' ? 'PRO' : 'FREE'}
            </p>
          </div>
          {user.is_admin && (
            <>
              <Link to="/admin" onClick={() => setOpen(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm text-white hover:bg-[#262626] no-underline" data-testid="menu-admin">
                <ShieldCheck size={14} weight="bold" className="text-[#666]" /> Admin Dashboard
              </Link>
              <Link to="/admin/posts" onClick={() => setOpen(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm text-white hover:bg-[#262626] no-underline" data-testid="menu-posts">
                <PencilSimple size={14} weight="bold" className="text-[#666]" /> Post Generator
              </Link>
            </>
          )}
          <Link to="/billing" onClick={() => setOpen(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm text-white hover:bg-[#262626] no-underline">
            <Lightning size={14} weight="bold" className="text-[#666]" /> {user.subscription_status === 'active' ? 'Manage Plan' : 'Upgrade to Pro'}
          </Link>
          <Link to="/referral" onClick={() => setOpen(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm text-white hover:bg-[#262626] no-underline">
            <UsersThree size={14} weight="bold" className="text-[#666]" /> Refer Friends
          </Link>
          <Link to="/api-keys" onClick={() => setOpen(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm text-white hover:bg-[#262626] no-underline">
            <Key size={14} weight="bold" className="text-[#666]" /> API Keys
          </Link>
          <Link to="/email-preferences" onClick={() => setOpen(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm text-white hover:bg-[#262626] no-underline border-b border-[#262626]">
            <Envelope size={14} weight="bold" className="text-[#666]" /> Email Preferences
          </Link>
          <button onClick={() => { setOpen(false); handleLogout(); }} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-[#ff3b30] hover:bg-[#262626] text-left" data-testid="user-logout-btn">
            <SignOut size={14} weight="bold" /> Sign Out
          </button>
        </div>
      )}
    </div>
  );
}

export default function Navbar({ user, leagues, activeLeague, leagueData, loadLeagueData, setActiveLeague, handleLogout, credits }) {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const handleSyncLeague = async () => {
    if (!activeLeague || syncing) return;
    setSyncing(true);
    try {
      await loadLeagueData(activeLeague, { silent: true });
      toast.success("League synced — rosters and values are up to date");
    } catch (e) {
      toast.error("Sync failed — please try again");
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    if (mobileOpen) document.body.classList.add('modal-open');
    else document.body.classList.remove('modal-open');
    return () => document.body.classList.remove('modal-open');
  }, [mobileOpen]);

  const allGroups = [...navGroups];

  const handleLeagueSwitch = (leagueId) => {
    setActiveLeague(leagueId);
    loadLeagueData(leagueId);
  };

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0a] border-b border-[#262626] h-14 sm:h-16" data-testid="navbar">
        <div className="max-w-[1600px] mx-auto h-full flex items-center justify-between px-3 sm:px-6">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 no-underline shrink-0" data-testid="nav-logo">
            <div className="w-7 h-7 sm:w-8 sm:h-8 bg-[#ccff00] flex items-center justify-center rounded-sm">
              <Trophy size={16} weight="bold" color="#000" />
            </div>
            <span className="font-heading text-xl sm:text-2xl tracking-tight text-white">
              DYNASTY TRADE<span className="text-[#ccff00]">MAKER</span>
            </span>
          </Link>

          {/* Desktop Nav — Grouped Dropdowns */}
          <div className="hidden lg:flex items-center gap-1">
            <Link
              to="/"
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold uppercase tracking-wider no-underline rounded-sm transition-colors
                ${location.pathname === '/' ? 'text-[#ccff00] bg-[#ccff00]/10' : 'text-[#a3a3a3] hover:text-white'}`}
              data-testid="nav-link-dashboard"
            >
              <Trophy size={15} weight="bold" />
              DASHBOARD
            </Link>
            {allGroups.map((group) => (
              <DesktopDropdown key={group.label} group={group} currentPath={location.pathname} />
            ))}
          </div>

          {/* Right side */}
          <div className="flex items-center gap-2">
            {leagues.length > 0 && (
              <Select value={activeLeague || ""} onValueChange={handleLeagueSwitch}>
                <SelectTrigger className="w-[130px] sm:w-[170px] h-8 sm:h-9 bg-[#171717] border-[#262626] text-xs sm:text-sm text-white" data-testid="league-selector" aria-label="Select league">
                  <SelectValue placeholder="Select league" />
                </SelectTrigger>
                <SelectContent className="bg-[#171717] border-[#262626]">
                  {leagues.map((l) => (
                    <SelectItem key={l.league_id} value={l.league_id} className="text-white hover:bg-[#262626]">{l.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {activeLeague && (
              <button
                onClick={handleSyncLeague}
                disabled={syncing}
                title="Re-sync league from Sleeper"
                aria-label="Re-sync league from Sleeper"
                className="hidden sm:flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 bg-[#171717] border border-[#262626] rounded-sm text-[#a3a3a3] hover:text-[#ccff00] hover:border-[#ccff00]/40 transition-colors disabled:opacity-60 shrink-0"
                data-testid="league-sync-btn"
              >
                <ArrowsClockwise size={15} weight="bold" className={syncing ? "animate-spin" : ""} />
              </button>
            )}
            {/* Credit counter for free users */}
            {credits && !credits.unlimited && credits.credits && (
              <div className="hidden sm:block relative group">
                <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-[10px] font-bold cursor-help ${
                  credits.credits.remaining <= 5 ? 'bg-[#ff3b30]/10 text-[#ff3b30] border border-[#ff3b30]/30' :
                  credits.credits.remaining <= 10 ? 'bg-[#fbbf24]/10 text-[#fbbf24] border border-[#fbbf24]/30' :
                  'bg-[#262626] text-[#a3a3a3] border border-[#262626]'
                }`} data-testid="credit-counter" aria-label={`${credits.credits.remaining} of ${credits.credits.total} free AI credits remaining`}>
                  <Lightning size={10} weight="bold" />
                  {credits.credits.remaining} CREDITS
                </div>
                {/* Tooltip */}
                <div className="absolute right-0 top-full mt-2 w-64 bg-[#171717] border border-[#262626] shadow-xl p-4 z-50 hidden group-hover:block">
                  <p className="text-xs text-white font-semibold mb-1">Free AI Credits</p>
                  <p className="text-[10px] text-[#a3a3a3] leading-relaxed mb-2">Each AI feature (Trade Analyzer, Finder, Simulator, Draft Advice, etc.) costs 1 credit. You get {credits.credits.total} free credits to explore the full tool.</p>
                  <div className="flex items-center justify-between text-[10px] mb-2">
                    <span className="text-[#666]">Used: {credits.credits.used}/{credits.credits.total}</span>
                    <span className={credits.credits.remaining <= 5 ? 'text-[#ff3b30]' : 'text-[#ccff00]'}>{credits.credits.remaining} remaining</span>
                  </div>
                  <div className="h-1.5 bg-[#0a0a0a] rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${(credits.credits.remaining / credits.credits.total) * 100}%`, backgroundColor: credits.credits.remaining <= 5 ? '#ff3b30' : credits.credits.remaining <= 10 ? '#fbbf24' : '#ccff00' }} />
                  </div>
                  <a href="/billing" className="block text-center text-[10px] text-[#ccff00] font-bold mt-3 no-underline hover:underline uppercase">Upgrade for unlimited →</a>
                </div>
              </div>
            )}
            {/* User section */}
            {user?.email ? (
              <UserMenu user={user} handleLogout={handleLogout} />
            ) : user ? (
              /* Guest (Sleeper-only) — show upgrade button */
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { window.location.href = '/api/auth/google/login'; }}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-white text-black font-bold text-[10px] rounded-sm hover:bg-gray-100 transition-colors"
                  data-testid="nav-google-signin"
                >
                  <GoogleLogo size={12} weight="bold" />
                  SIGN IN
                </button>
              </div>
            ) : null}
            <button onClick={() => setMobileOpen(true)} className="lg:hidden p-2 text-[#a3a3a3] hover:text-white" data-testid="mobile-menu-btn">
              <ListBullets size={22} weight="bold" />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Slide-out — Grouped Sections */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden" data-testid="mobile-menu">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
          <div className="absolute right-0 top-0 bottom-0 w-72 bg-[#0a0a0a] border-l border-[#262626] flex flex-col animate-slideIn">
            <div className="flex items-center justify-between p-4 border-b border-[#262626]">
              <span className="font-heading text-lg text-white uppercase">MENU</span>
              <button onClick={() => setMobileOpen(false)} className="text-[#a3a3a3] hover:text-white" data-testid="mobile-menu-close">
                <X size={20} weight="bold" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {/* Dashboard */}
              <Link to="/" onClick={() => setMobileOpen(false)}
                className={`flex items-center gap-3 px-5 py-3.5 text-sm font-semibold uppercase tracking-wider no-underline border-b border-[#1a1a1a]
                  ${location.pathname === '/' ? 'text-[#ccff00] bg-[#ccff00]/10' : 'text-[#a3a3a3] hover:text-white'}`}>
                <Trophy size={18} weight="bold" /> DASHBOARD
              </Link>

              {/* Grouped sections */}
              {allGroups.map((group) => {
                const isExpanded = mobileExpanded === group.label;
                const GIcon = group.icon;
                const hasActive = group.items.some(i => location.pathname === i.path);
                return (
                  <div key={group.label}>
                    <button
                      onClick={() => setMobileExpanded(isExpanded ? null : group.label)}
                      className={`w-full flex items-center justify-between px-5 py-3.5 text-sm font-semibold uppercase tracking-wider border-b border-[#1a1a1a]
                        ${hasActive ? 'text-[#ccff00]' : 'text-[#a3a3a3]'}`}
                    >
                      <div className="flex items-center gap-3">
                        <GIcon size={18} weight="bold" />
                        {group.label}
                      </div>
                      <CaretDown size={14} className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                    </button>
                    {isExpanded && (
                      <div className="bg-[#171717]">
                        {group.items.map(({ path, label, desc, icon: ItemIcon }) => (
                          <Link
                            key={path}
                            to={path}
                            onClick={() => setMobileOpen(false)}
                            className={`flex items-start gap-3 px-8 py-3 no-underline border-b border-[#1a1a1a] transition-colors
                              ${location.pathname === path ? 'text-[#ccff00] bg-[#ccff00]/5' : 'text-white hover:bg-[#262626]'}`}
                          >
                            <ItemIcon size={14} weight="bold" className="mt-0.5 shrink-0 text-[#666]" />
                            <div>
                              <p className="text-sm">{label}</p>
                              <p className="text-[10px] text-[#666]">{desc}</p>
                            </div>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Leagues link */}
              <Link to="/leagues" onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-5 py-3.5 text-sm font-semibold uppercase tracking-wider no-underline text-[#a3a3a3] hover:text-white border-b border-[#1a1a1a]">
                <Trophy size={18} weight="bold" /> LEAGUES
              </Link>

              {/* Re-sync league */}
              {activeLeague && (
                <button
                  onClick={async () => { await handleSyncLeague(); setMobileOpen(false); }}
                  disabled={syncing}
                  className="w-full flex items-center gap-3 px-5 py-3.5 text-sm font-semibold uppercase tracking-wider text-[#a3a3a3] hover:text-white border-b border-[#1a1a1a] disabled:opacity-60"
                  data-testid="mobile-league-sync-btn"
                >
                  <ArrowsClockwise size={18} weight="bold" className={syncing ? "animate-spin" : ""} />
                  {syncing ? "SYNCING…" : "RE-SYNC LEAGUE"}
                </button>
              )}

              {/* Google Sign-in for guests */}
              {user && !user.email && (
                <div className="px-5 py-4 border-t border-[#262626]">
                  <p className="text-[10px] text-[#666] mb-2">Sign in with Google to unlock all Pro features</p>
                  <button
                    onClick={() => { window.location.href = '/api/auth/google/login'; }}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-white text-black font-bold text-xs rounded-sm hover:bg-gray-100"
                    data-testid="mobile-google-signin"
                  >
                    <GoogleLogo size={14} weight="bold" />
                    SIGN IN WITH GOOGLE
                  </button>
                </div>
              )}
              {user?.email && (
                <div className="px-5 py-3 border-t border-[#262626] flex items-center gap-3">
                  {user.picture && <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">{user.name}</p>
                    <p className="text-[10px] text-[#666] truncate">{user.email}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
